import React from 'react';
import { Formik, Field } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import LabeledInput from '../../mdc/LabeledInput';
import SelectInput, { Option } from '../../mdc/SelectInput';
import * as RDBCase from '../../services/buildfire/rdb/cases';
import Typography from '../common/Typography';
import TextArea from '../common/TextArea';
import Bool from '../../constants/bool';
import { SpayNeuterLabel } from '../../constants/spayNeuter';
import { uppercaseFirstLetter } from '../../util/string';
import Form from '../common/Form';
import Platform from '../common/Platform';
import ExpandingButton from '../common/ExpandingButton';

interface PatientInfoFormProps {
  case?: RDBCase.Case | FormValues;
  title?: string;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => Promise<void>;
  submitText?: string;
  renderButtons?: (props: { disabled: boolean }) => React.ReactNode;
}

export interface FormValues {
  patientFirstName: string;
  patientLastName: string;
  age: number;
  ageMonths?: number;
  sex: RDBCase.Sex;
  fixed: '' | Bool.TRUE | Bool.FALSE;
  species: RDBCase.AllowedSpecies;
  description: string;
  internalReferenceNumber?: string;
}

const Container = styled.div`
  padding: 16px 0 32px 0;
`;

const Padding = styled.div`
  padding: 0 8px;
`;

const SubmitButtonContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
  padding: 0 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubmitButton = styled(ExpandingButton)`
  margin-top: 32px;
  margin-bottom: 16px;
`;

const SplitInputContainer = styled.div`
  width: 100%;
`;

const SplitContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0 -8px;
  > ${SplitInputContainer} {
    margin: 0 8px 0 0 !important;

    &:first-child {
      margin: 0 8px !important;
    }
  }
`;

const Title: React.FC<{}> = ({ children }) => (
  <Typography
    as="h1"
    variant="secondary"
    fontSize="24px"
    fontWeight="bold"
    textAlign="center"
    uppercase
    margin="32px 0"
    letterSpacing="2px"
  >
    {children}
  </Typography>
);

const PatientInfoForm: React.FC<PatientInfoFormProps> = ({
  case: c,
  title = 'Patient Info',
  onSubmit,
  submitText = 'Submit',
  renderButtons,
  initialValues = {},
  ...props
}) => {
  return (
    <Container>
      <Formik
        initialValues={
          {
            patientFirstName: initialValues.patientFirstName || ('' as any),
            patientLastName: initialValues.patientLastName || ('' as any),
            internalReferenceNumber:
              initialValues.internalReferenceNumber || ('' as any),
            age: initialValues.age || ('' as any),
            ageMonths: initialValues.ageMonths || ('' as any),
            sex: initialValues.sex || ('' as any),
            description: initialValues.description || ('' as any),
            species: initialValues.species || ('' as any),
            fixed:
              initialValues.fixed === undefined
                ? ('' as any)
                : initialValues.fixed,
          } as FormValues
        }
        onSubmit={onSubmit}
        validationSchema={Yup.object({
          patientFirstName: Yup.string().min(1).max(50).required('Required'),
          patientLastName: Yup.string().min(1).max(50).required('Required'),
          internalReferenceNumber: Yup.string().optional(),
          age: Yup.number().min(0).max(99).required('Required'),
          ageMonths: Yup.number().min(0).max(11).optional(),
          species: Yup.string()
            .oneOf([
              RDBCase.AllowedSpecies.DOG,
              RDBCase.AllowedSpecies.CAT,
              RDBCase.AllowedSpecies.HORSE,
            ])
            .required('Required'),
          sex: Yup.string()
            .oneOf([RDBCase.Sex.MALE, RDBCase.Sex.FEMALE])
            .required('Required'),
          fixed: Yup.string()
            .oneOf([Bool.TRUE, Bool.FALSE])
            .required('Required'),
          description: Yup.string().max(3000).optional(),
        })}
        validateOnMount
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          isValid,
          isSubmitting,
          setFieldTouched,
        }) => {
          return (
            <Form>
              <Padding>
                <Typography
                  as="h3"
                  fontSize="20px"
                  variant="secondary"
                  margin="16px 0"
                >
                  {title}
                </Typography>
                <Field
                  as={LabeledInput}
                  label="Patient first name"
                  error={
                    touched.patientFirstName &&
                    uppercaseFirstLetter(errors.patientFirstName as string)
                  }
                  name="patientFirstName"
                />
                <Field
                  as={LabeledInput}
                  label="Patient last name"
                  error={
                    touched.patientLastName &&
                    uppercaseFirstLetter(errors.patientLastName as string)
                  }
                  name="patientLastName"
                />
                <Field
                  as={LabeledInput}
                  label="Reference Number"
                  type="internalReferenceNumber"
                  inputMode="internalReferenceNumber"
                  error={
                    touched.internalReferenceNumber &&
                    uppercaseFirstLetter(
                      errors.internalReferenceNumber as string
                    )
                  }
                  name="internalReferenceNumber"
                  helpText="(Optional) For your records"
                />

                <SplitContainer>
                  <SplitInputContainer>
                    <Field
                      as={LabeledInput}
                      label="Age"
                      name="age"
                      type="number"
                      min={0}
                      max={99}
                      error={
                        touched.age &&
                        uppercaseFirstLetter(errors.age as string)
                      }
                      suffix="YEARS"
                    />
                  </SplitInputContainer>
                  <SplitInputContainer>
                    <Field
                      as={LabeledInput}
                      label="Age"
                      name="ageMonths"
                      type="number"
                      min={0}
                      max={12}
                      helpText="Optional"
                      suffix="MONTHS"
                      error={
                        touched.ageMonths &&
                        uppercaseFirstLetter(errors.ageMonths as string)
                      }
                    />
                  </SplitInputContainer>
                </SplitContainer>
                <SelectInput
                  label="Species"
                  name="species"
                  value={values.species}
                  handleChange={(newValue) => {
                    if (newValue !== values.species) {
                      setFieldTouched('species', true);
                      setFieldValue('species', newValue);
                    }
                  }}
                  error={
                    touched.species
                      ? uppercaseFirstLetter(errors.species as string)
                      : ''
                  }
                >
                  <Option value={RDBCase.AllowedSpecies.DOG}>Dog</Option>
                  <Option value={RDBCase.AllowedSpecies.CAT}>Cat</Option>
                  <Option value={RDBCase.AllowedSpecies.HORSE}>Horse</Option>
                </SelectInput>
                <SelectInput
                  label="Sex"
                  name="gender"
                  value={values.sex}
                  handleChange={(newValue) => {
                    if (newValue !== values.sex) {
                      setFieldTouched('sex', true);
                      setFieldValue('sex', newValue);
                    }
                  }}
                  error={
                    touched.sex
                      ? uppercaseFirstLetter(errors.sex as string)
                      : ''
                  }
                >
                  <Option value={RDBCase.Sex.MALE}>Male</Option>
                  <Option value={RDBCase.Sex.FEMALE}>Female</Option>
                </SelectInput>
                <SelectInput
                  label={
                    SpayNeuterLabel[values.sex as RDBCase.Sex] ||
                    SpayNeuterLabel.male
                  }
                  name="fixed"
                  value={values.fixed}
                  handleChange={(newValue) => {
                    if (newValue !== values.fixed) {
                      setFieldTouched('fixed', true);
                      setFieldValue('fixed', newValue);
                    }
                  }}
                  error={
                    touched.fixed
                      ? uppercaseFirstLetter(errors.fixed as string)
                      : ''
                  }
                >
                  <Option value={Bool.TRUE}>Yes</Option>
                  <Option value={Bool.FALSE}>No</Option>
                </SelectInput>
                <Field
                  as={TextArea}
                  label="Relevant clinical history"
                  name="description"
                  rows={4}
                  resizable={false}
                  maxLength={3000}
                  helpText="Optional"
                  error={
                    touched.description &&
                    uppercaseFirstLetter(errors.description as string)
                  }
                />
              </Padding>
              <SubmitButtonContainer>
                {renderButtons ? (
                  renderButtons({ disabled: !isValid || isSubmitting })
                ) : (
                  <SubmitButton
                    fullWidth
                    buttonType="raised"
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    {submitText}
                  </SubmitButton>
                )}
              </SubmitButtonContainer>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default PatientInfoForm;
